import { useRef, useState, useEffect } from 'react';
import { Modal, Button, Divider, Tag, InputNumber, Space, Typography } from 'antd';
import { FileDoneOutlined, PrinterOutlined, FilePdfOutlined } from '@ant-design/icons';

const { Text } = Typography;

const MONTHS = [
  'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
  'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr',
];

const formatPrice = (p) => {
  if (!p && p !== 0) return '0';
  return Number(p).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// ── O'zbekcha summa -> so'z (butun son) ──
const ONES = ['', 'bir', 'ikki', 'uch', "to'rt", 'besh', 'olti', 'yetti', 'sakkiz', "to'qqiz"];
const TENS = ['', "o'n", 'yigirma', "o'ttiz", 'qirq', 'ellik', 'oltmish', 'yetmish', 'sakson', "to'qson"];
const SCALES = ['', 'ming', 'million', 'milliard', 'trillion'];

const threeDigits = (num) => {
  const parts = [];
  const h = Math.floor(num / 100);
  const t = Math.floor((num % 100) / 10);
  const o = num % 10;
  if (h) parts.push(ONES[h] + ' yuz');
  if (t) parts.push(TENS[t]);
  if (o) parts.push(ONES[o]);
  return parts.join(' ');
};

const somToWords = (value) => {
  let n = Math.floor(Number(value) || 0);
  if (n === 0) return 'nol';
  const groups = [];
  while (n > 0) { groups.push(n % 1000); n = Math.floor(n / 1000); }
  const words = [];
  for (let g = groups.length - 1; g >= 0; g--) {
    if (groups[g] === 0) continue;
    let part = threeDigits(groups[g]);
    if (SCALES[g]) part += ' ' + SCALES[g];
    words.push(part);
  }
  return words.join(' ').trim();
};

export default function OrderContractModal({ open, onClose, order }) {
  const printRef = useRef();
  const [advancePct, setAdvancePct] = useState(30);

  useEffect(() => { if (open) setAdvancePct(30); }, [open]);

  if (!order) return null;

  const contractNo = `QK-${order.year || new Date().getFullYear()}-${order.id || '—'}`;
  const todayDate = new Date().toLocaleDateString('uz-UZ');
  const eventDate = order.day && order.month && order.year
    ? `${order.day}-${MONTHS[(order.month - 1) % 12]} ${order.year}` : '—';
  const timeRange = order.start
    ? `${String(order.start).slice(0, 5)} — ${String(order.end).slice(0, 5)}`
    : '—';

  const serviceName = order.service_name || '—';
  const providerName = order.business_name || '—';
  const providerPhone = order.business_phone || '—';
  const providerAddress = order.business_address || '—';
  const providerStir = order.business_stir || '—';
  const clientName = order.client_name || "Noma'lum mijoz";
  const clientPhone = order.client_phone || '—';
  const city = (order.business_address || '').split(',')[0]?.trim() || "Toshkent";

  const total = Number(order.summa) || 0;
  const paid = Number(order.payments_total) || 0;
  const advance = Math.round(total * (Number(advancePct) || 0) / 100);
  const afterAdvance = Math.max(0, total - advance);

  const handlePrint = () => {
    const content = printRef.current;
    const win = window.open('', '_blank', 'width=900,height=700');
    win.document.write(`
      <html><head>
        <title>Shartnoma ${contractNo}</title>
        <style>
          * { margin:0; padding:0; box-sizing:border-box; }
          body { font-family: "Times New Roman", serif; font-size: 13px; padding: 40px; color:#111; }
          h1 { text-align:center; font-size:18px; letter-spacing:2px; margin-bottom:4px; }
          h2 { font-size:13px; font-weight:700; text-align:center; margin:16px 0 8px; text-transform:uppercase; }
          p  { line-height:1.9; text-align:justify; margin-bottom:4px; }
          ul { margin:4px 0 8px 60px; }
          li { line-height:1.7; }
          table { width:100%; border-collapse:collapse; margin:12px 0; }
          td { padding:6px 8px; border:1px solid #ccc; font-size:13px; }
          @page { size:A4; margin:18mm; }
        </style>
      </head><body>${content.innerHTML}</body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
  };

  const td = { padding: '7px 10px', border: '1px solid #e5e7eb' };
  const tdLabel = { ...td, background: '#fafafa', fontWeight: 600, width: '55%' };
  const h2s = { fontFamily: 'Times New Roman', fontSize: 13, fontWeight: 700, textAlign: 'center', margin: '16px 0 8px', textTransform: 'uppercase' };
  const ps = { fontFamily: 'Times New Roman', fontSize: 13, lineHeight: 1.9, textIndent: 40 };
  const liS = { lineHeight: 1.7, fontSize: 13 };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={800}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FileDoneOutlined style={{ color: '#D4AF37', fontSize: 20 }} />
          <span>Shartnoma {contractNo}</span>
          <Tag color="gold" style={{ marginLeft: 8 }}>Buyurtma #{order.id}</Tag>
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <Space>
            <Text type="secondary" style={{ fontSize: 13 }}>Avans foizi:</Text>
            <InputNumber min={0} max={100} value={advancePct} onChange={(v) => setAdvancePct(v ?? 0)}
              addonAfter="%" style={{ width: 110 }} />
          </Space>
          <Space>
            <Button onClick={onClose}>Yopish</Button>
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>Chop etish</Button>
            <Button type="primary" icon={<FilePdfOutlined />} onClick={handlePrint}
              style={{ background: 'linear-gradient(135deg,#5F33E1,#8B5CF6)', border: 'none' }}>
              PDF saqlash
            </Button>
          </Space>
        </div>
      }
      styles={{ body: { padding: '16px 24px', maxHeight: '66vh', overflowY: 'auto', background: '#fff' } }}
    >
      <div ref={printRef} style={{ color: '#111', fontFamily: 'Times New Roman' }}>
        <h1 style={{ textAlign: 'center', fontSize: 18, letterSpacing: 2, marginBottom: 4 }}>
          XIZMAT KO'RSATISH SHARTNOMASI
        </h1>
        <p style={{ textAlign: 'center', color: '#888', fontSize: 13, marginBottom: 16 }}>
          {serviceName} xizmati bo'yicha pudrat shartnomasi
        </p>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 16 }}>
          <span>{city} sh.</span>
          <span>№ <b>{contractNo}</b></span>
          <span>Sana: <b>{todayDate}</b></span>
        </div>

        <Divider style={{ margin: '12px 0', borderColor: '#D4AF37' }} />

        <p style={{ fontSize: 13, lineHeight: 1.9, textAlign: 'justify', marginBottom: 12 }}>
          Ushbu shartnoma kelgusida <b>"Buyurtmachi"</b> deb ataluvchi <b>{clientName}</b> ({clientPhone}) hamda{' '}
          <b>"Xizmat ko'rsatuvchi"</b> deb ataluvchi <b>{providerName}</b> o'rtasida quyidagilar to'g'risida tuzildi:
        </p>

        {/* I */}
        <h2 style={h2s}>I. SHARTNOMANING MAZMUNI</h2>
        <p style={ps}>
          1.1. "Xizmat ko'rsatuvchi" "Buyurtmachi"ning <b>{eventDate}</b> kuni soat <b>{timeRange}</b> oralig'ida{' '}
          <b>{serviceName}</b> xizmatini ko'rsatish majburiyatini, "Buyurtmachi" esa ushbu xizmatni qabul qilib olish va haqini to'lash majburiyatini oladi.
        </p>
        <p style={ps}>1.2. Buyurtma hajmi (soni / mehmonlar): <b>{order.quantity || 1}</b>.{order.note ? ` Qo'shimcha shartlar: ${order.note}.` : ''}</p>

        {/* II */}
        <h2 style={h2s}>II. SHARTNOMA BAHOSI VA HISOB-KITOB TARTIBI</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 13 }}>
          <tbody>
            <tr><td style={tdLabel}>Jami shartnoma summasi</td><td style={{ ...td, fontWeight: 700 }}>{formatPrice(total)} so'm</td></tr>
            <tr><td style={{ ...tdLabel, background: '#fffbeb' }}>Avans to'lov ({advancePct}%)</td><td style={{ ...td, color: '#D97706', fontWeight: 600 }}>{formatPrice(advance)} so'm</td></tr>
            <tr><td style={{ ...tdLabel, background: '#f0fdf4' }}>Xizmat ko'rsatilgandan keyin</td><td style={{ ...td, color: '#16a34a', fontWeight: 600 }}>{formatPrice(afterAdvance)} so'm</td></tr>
            {paid > 0 && <tr><td style={tdLabel}>Shu jumladan to'langan</td><td style={td}>{formatPrice(paid)} so'm</td></tr>}
          </tbody>
        </table>
        <p style={ps}>
          2.1. Shartnoma bo'yicha umumiy summa <b>{formatPrice(total)} ({somToWords(total)}) so'm</b>ni tashkil etadi.
        </p>
        <p style={ps}>
          2.2. "Buyurtmachi" shartnoma imzolangandan so'ng <b>{advancePct}%</b> miqdorida avans, ya'ni{' '}
          <b>{formatPrice(advance)} ({somToWords(advance)}) so'm</b> to'laydi.
        </p>
        <p style={ps}>
          2.3. Qolgan <b>{formatPrice(afterAdvance)} so'm</b> xizmat ko'rsatilgan kungacha to'liq to'lanadi.
        </p>

        {/* III */}
        <h2 style={h2s}>III. TOMONLARNING HUQUQ VA MAJBURIYATLARI</h2>
        <p style={ps}>3.1. "Xizmat ko'rsatuvchi" majburiyatlari:</p>
        <ul style={{ margin: '4px 0 8px 60px' }}>
          <li style={liS}>kelishilgan sana va vaqtda xizmatni sifatli va to'liq ko'rsatish;</li>
          <li style={liS}>shartnomada belgilangan hajm va shartlarga to'liq rioya qilish;</li>
          <li style={liS}>xizmat ko'rsatishga to'sqinlik qiluvchi holatlar haqida "Buyurtmachi"ni o'z vaqtida xabardor qilish.</li>
        </ul>
        <p style={ps}>3.2. "Buyurtmachi" majburiyatlari:</p>
        <ul style={{ margin: '4px 0 8px 60px' }}>
          <li style={liS}>shartnomada belgilangan to'lovlarni o'z vaqtida amalga oshirish;</li>
          <li style={liS}>xizmatni qabul qilib olish va zarur shart-sharoitlarni ta'minlash;</li>
          <li style={liS}>buyurtma shartlaridagi o'zgarishlar haqida oldindan xabar berish.</li>
        </ul>
        <p style={ps}>3.3. Tomonlar o'zaro hurmat, halollik va hamkorlik tamoyillariga amal qiladi.</p>

        {/* IV */}
        <h2 style={h2s}>IV. TOMONLARNING JAVOBGARLIGI</h2>
        <p style={ps}>4.1. "Xizmat ko'rsatuvchi" muddatni buzganlik uchun har bir kechiktirilgan kun uchun shartnoma summasining 0,5% miqdorida penya to'laydi.</p>
        <p style={ps}>4.2. "Buyurtmachi" to'lovni o'z vaqtida amalga oshirmasa, kechiktirilgan summaning 0,4% miqdorida penya to'laydi.</p>

        {/* V */}
        <h2 style={h2s}>V. SHARTNOMANI BEKOR QILISH VA AVANSNI QAYTARISH</h2>
        <p style={ps}>5.1. "Buyurtmachi" tadbirdan kamida 7 (yetti) kun oldin yozma ravishda bekor qilsa, avansning 50% i qaytariladi.</p>
        <p style={ps}>5.2. Tadbirdan 7 kundan kam vaqt qolganda bekor qilingan taqdirda avans qaytarilmaydi.</p>
        <p style={ps}>5.3. Xizmat "Xizmat ko'rsatuvchi" aybi bilan ko'rsatilmasa, olingan avans "Buyurtmachi"ga to'liq qaytariladi.</p>

        {/* VI */}
        <h2 style={h2s}>VI. FORS-MAJOR</h2>
        <p style={ps}>6.1. Tabiiy ofatlar yoki boshqa yengib bo'lmaydigan kuch holatlari majburiyatlarni bajarmaslikda taraflarni javobgarlikdan ozod etadi.</p>

        {/* VII */}
        <h2 style={h2s}>VII. NIZOLARNI HAL QILISH</h2>
        <p style={ps}>7.1. Tomonlar o'rtasidagi nizolar o'zaro muzokaralar yo'li bilan hal etiladi.</p>
        <p style={ps}>7.2. Kelishuvga erishilmagan taqdirda nizolar O'zbekiston Respublikasi qonunchiligiga muvofiq sud tartibida hal qilinadi.</p>

        {/* VIII */}
        <h2 style={h2s}>VIII. YAKUNIY QOIDALAR</h2>
        <p style={ps}>8.1. Shartnoma 2 (ikki) nusxada, har ikkala tomon uchun bir xil yuridik kuchga ega tarzda tuzildi.</p>
        <p style={ps}>8.2. Shartnoma tomonlar imzolagan kundan kuchga kiradi va <b>{eventDate}</b> xizmat ko'rsatilib, to'liq hisob-kitob amalga oshirilgunga qadar amal qiladi.</p>

        <Divider style={{ margin: '20px 0 12px', borderColor: '#e5e7eb' }} />

        {/* Imzolar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 13 }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 700, marginBottom: 8 }}>BUYURTMACHI:</p>
            <p>{clientName}</p>
            <p>Tel: {clientPhone}</p>
            <div style={{ marginTop: 40, borderTop: '1px solid #000', paddingTop: 4, fontSize: 12, color: '#888' }}>Imzo / Sana</div>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 700, marginBottom: 8 }}>XIZMAT KO'RSATUVCHI:</p>
            <p>{providerName}</p>
            <p>{serviceName}</p>
            {providerStir !== '—' && <p>STIR: {providerStir}</p>}
            {providerAddress !== '—' && <p>Manzil: {providerAddress}</p>}
            {providerPhone !== '—' && <p>Tel: {providerPhone}</p>}
            <div style={{ marginTop: 40, borderTop: '1px solid #000', paddingTop: 4, fontSize: 12, color: '#888' }}>Imzo / Sana / M.O'.</div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
